import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  IActivo,
  ICronograma,
  IDespacho,
  IQueryParam,
  IRecorrido,
  IUpdateDespacho,
  IUsuario,
  Periodo,
} from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { DespachosService } from '../despachos.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';

@Component({
  selector: 'app-crear-editar-despacho',
  templateUrl: './crear-editar-despacho.component.html',
  styleUrl: './crear-editar-despacho.component.scss',
  standalone: false,
})
export class CrearEditarDespachoComponent implements OnInit, OnDestroy {
  private lista2 = inject(Lista2Service);
  private dialog = inject(DialogService);
  private service = inject(DespachosService);
  helper = inject(HelperService);

  private data = inject(MAT_DIALOG_DATA) as IDespacho;
  readonly dialogRef = inject(MatDialogRef<CrearEditarDespachoComponent>);
  public loading = signal(false);
  public title?: string;

  public cronograma?: ICronograma;
  public cronogramas: ICronograma[] = [];
  public cronogramas$?: Subscription;

  public despacho?: IDespacho;

  public vehiculos: IActivo[] = [];
  public vehiculos$?: Subscription;

  public recorridos: IRecorrido[] = [];
  public recorridos$?: Subscription;

  public form?: FormGroup;

  private createForm() {
    this.form = new FormGroup({
      idCronograma: new FormControl(
        this.despacho?.idCronograma,
        Validators.required,
      ),
      salio: new FormControl(this.despacho?.salio, Validators.required),
      idActivo: new FormControl(this.despacho?.idActivo, Validators.required),
      idChofer: new FormControl(this.despacho?.idChofer),
      idsRecorridos: new FormControl(
        this.despacho?.idsRecorridos,
        Validators.required,
      ),
      completado: new FormControl(false),
      cancelado: new FormControl(this.despacho?.cancelado || false),
    });
  }

  private getClosestTime(): string {
    const times =
      this.cronograma?.periodos.map((periodo) => periodo.desde) || [];
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();

    let closestTime = times[0];
    let closestDiff = Infinity;

    for (const time of times) {
      const [hours, minutes] = time.split(':').map(Number);
      const timeMinutes = hours * 60 + minutes;

      // Calculamos la diferencia absoluta en minutos
      const diff = Math.abs(timeMinutes - nowMinutes);

      if (diff < closestDiff) {
        closestDiff = diff;
        closestTime = time;
      }
    }

    return closestTime;
  }

  public cambioCronograma(cronograma: ICronograma) {
    this.cronograma = cronograma;
    const closestTime = this.getClosestTime();
    this.form?.patchValue({ salio: closestTime });
    const periodo = this.cronograma?.periodos.find(
      (periodo) => periodo.desde === closestTime,
    );
    this.cambioHoraSalida(periodo);
  }

  public cambioHoraSalida(periodo: Periodo) {
    const datos = periodo.datos as {
      idVehiculo: string;
      idChofer: string;
      idsRecorridos: string[];
      idRecorridoActual: string;
    };
    this.form?.patchValue({ idActivo: datos.idVehiculo });
    this.form?.patchValue({ idChofer: datos.idChofer });
    this.form?.patchValue({ idsRecorridos: datos.idsRecorridos });
  }

  // Listados
  private async listar() {
    if (!this.data?._id) return;
    // Listar despacho
    this.despacho = await firstValueFrom(this.lista2.despacho(this.data._id));

  }

  private async listarVehiculos() {
    const query: IQueryParam = {
      sort: 'identificacion',
      limit: 0,
      select: 'identificacion vehiculo.patente',
    };
    this.vehiculos$?.unsubscribe();
    this.vehiculos$ = this.lista2.vehiculos(query, 30).subscribe((data) => {
      this.vehiculos = data.datos;
    });
  }

  private async listarRecorridos() {
    const query: IQueryParam = {
      sort: 'nombre',
      limit: 0,
      select: 'nombre nombreFlota destino por',
      includeChildren: true,
    };
    this.recorridos$?.unsubscribe();
    this.recorridos$ = this.lista2.recorridos(query, 30).subscribe((data) => {
      this.recorridos = data.datos;
    });
  }

  private async listarCronogramas() {
    const query: IQueryParam = {
      sort: 'nombre',
      limit: 0,
    };
    this.cronogramas$?.unsubscribe();
    this.cronogramas$ = this.lista2.cronogramas(query, 30).subscribe((data) => {
      this.cronogramas = data.datos;
    });
  }

  public async submit() {
    const res = await this.dialog.confirm(
      'Editar',
      '¿Querés editar el despacho?',
    );
    if (!res) return;
    try {
      let res;
      if (this.data?._id) {
        // Editar
        this.loading.set(true);
        const data = this.form?.value as IUpdateDespacho;
        res = await this.service.update(this.data?._id, data);
        this.helper.notifSuccess('Despacho editado correctamente');
      } else {
        // Crear
        this.loading.set(true);
        const data = this.form?.value as IDespacho;
        res = await this.service.create(data);
        this.helper.notifSuccess('Despacho creado correctamente');
      }
      this.dialogRef.close(res);
    } catch (error) {
      console.error(`Error al editar despacho`, error);
      this.helper.notifError(error);
    } finally {
      this.loading.set(false);
    }
  }

  async ngOnInit() {
    this.loading.set(true);
    this.title = this.data ? 'Editar Despacho' : 'Nuevo Despacho';
    this.createForm();
    await Promise.all([
      this.listar(),
      this.listarVehiculos(),
      this.listarRecorridos(),
      this.listarCronogramas(),
    ]);
    this.loading.set(false);
  }

  ngOnDestroy(): void {
    this.vehiculos$?.unsubscribe();
    this.recorridos$?.unsubscribe();
    this.cronogramas$?.unsubscribe();
  }
}
